import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import { Calculator, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';

const LEAVE_TYPES = [
  'Casual Leave', 'Earned Leave', 'Medical Leave', 'Maternity Leave',
  'Paternity Leave', 'Study Leave', 'Academic Leave', 'On-Duty', 'Vacation Leave',
  'Half Day', 'Compensatory Off'
];

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

export const RecalculateBalancesDialog = ({ selectedEntity, onDone }) => {
  const [open, setOpen] = useState(false);
  const [leaveType, setLeaveType] = useState('Casual Leave');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [defaultTotal, setDefaultTotal] = useState('');
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const call = async (apply) => {
    const body = {
      leave_type: leaveType, year, apply,
      entity_id: selectedEntity?.entity_id || null,
    };
    if (defaultTotal !== '') body.default_total = parseFloat(defaultTotal);
    const cleanOverrides = {};
    Object.entries(overrides).forEach(([id, v]) => {
      if (v !== '' && !isNaN(parseFloat(v))) cleanOverrides[id] = parseFloat(v);
    });
    if (apply && Object.keys(cleanOverrides).length > 0) body.overrides = cleanOverrides;
    const res = await fetch(`${API_URL}/api/leaves/balances/recalculate`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(body)
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.detail || 'Request failed');
    return d;
  };

  const handlePreview = async () => {
    setLoading(true);
    setOverrides({});
    try { setPreview(await call(false)); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const d = await call(true);
      toast.success(`Recalculated ${leaveType} for ${d.applied} employee(s)`);
      setOpen(false); setPreview(null);
      onDone && onDone();
    } catch (e) { toast.error(e.message); }
    finally { setApplying(false); }
  };

  const rows = preview?.rows || [];
  const changedCount = preview?.summary?.changed || 0;
  const skippedCount = preview?.summary?.skipped || 0;
  const overriddenSkipped = rows.filter(r => r.skipped && overrides[r.employee_id] !== undefined && overrides[r.employee_id] !== '').length;
  const applyCount = rows.length - skippedCount + overriddenSkipped;

  const effTotal = (r) => {
    const ov = overrides[r.employee_id];
    if (ov !== undefined && ov !== '' && !isNaN(parseFloat(ov))) return parseFloat(ov);
    return r.skipped ? null : r.new_total;
  };
  const effBalance = (r) => {
    const t = effTotal(r);
    return t == null ? null : Math.max(Math.round((t - (r.used || 0)) * 10) / 10, 0);
  };

  return (
    <>
      <Button variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        onClick={() => { setOpen(true); setPreview(null); }} data-testid="recalc-balances-btn">
        <Calculator className="w-4 h-4 mr-1.5" />Recalculate
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPreview(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recalculate Leave Balances</DialogTitle>
            <DialogDescription>
              Rebuilds balances from approved leave requests: Total comes from each employee's leave policy
              (or the default below), Used = approved paid days in the year, Balance = Total − Used.
              After Preview you can EDIT any employee's total individually. Nothing changes until you click Apply.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Leave Category</Label>
              <Select value={leaveType} onValueChange={(v) => { setLeaveType(v); setPreview(null); }}>
                <SelectTrigger data-testid="recalc-leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Year</Label>
              <Input value={year} onChange={e => { setYear(e.target.value); setPreview(null); }}
                data-testid="recalc-year" />
            </div>
            <div className="space-y-1.5">
              <Label>Default Total <span className="text-slate-400 text-xs">(if no policy)</span></Label>
              <Input type="number" step="0.5" placeholder="e.g. 12" value={defaultTotal}
                onChange={e => { setDefaultTotal(e.target.value); setPreview(null); }}
                data-testid="recalc-default-total" />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <Button onClick={handlePreview} disabled={loading} data-testid="recalc-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Calculator className="w-4 h-4 mr-1.5" />}
              Preview
            </Button>
            {preview && (
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="border-slate-300">{rows.length} employees</Badge>
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">{changedCount} will change</Badge>
                {skippedCount > 0 && <Badge className="bg-red-100 text-red-700 border-red-200">{skippedCount} skipped (no policy/default)</Badge>}
              </div>
            )}
          </div>

          {preview && (
            <div className="border rounded-md mt-2 max-h-[45vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Dept</TableHead>
                    <TableHead className="text-center">Current<p className="text-[9px] font-normal text-slate-400">bal / total</p></TableHead>
                    <TableHead className="text-center">Used ({preview.year})</TableHead>
                    <TableHead className="text-center">New<p className="text-[9px] font-normal text-slate-400">bal / total (editable)</p></TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.employee_id} data-testid={`recalc-row-${r.employee_id}`}
                      className={r.skipped ? 'bg-red-50' : r.changed ? 'bg-amber-50' : ''}>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}</TableCell>
                      {r.skipped ? (
                        <>
                          <TableCell colSpan={2} className="text-xs text-red-600">
                            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />No policy — enter a total →
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs font-semibold text-emerald-700 w-8 text-right">
                                {effBalance(r) != null ? effBalance(r) : '—'} /
                              </span>
                              <Input type="number" step="0.5" className="h-7 w-16 text-xs text-center"
                                placeholder="?" value={overrides[r.employee_id] ?? ''}
                                onChange={e => setOverrides({ ...overrides, [r.employee_id]: e.target.value })}
                                data-testid={`recalc-total-${r.employee_id}`} />
                            </div>
                          </TableCell>
                          <TableCell className="text-[10px] text-slate-400">
                            {overrides[r.employee_id] ? 'manual' : 'skipped'}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-center text-xs text-slate-500">
                            {r.old_total != null ? `${r.old_balance ?? '—'} / ${r.old_total}` : <span className="text-red-400">missing</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">{r.used}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs font-semibold text-emerald-700 w-8 text-right">{effBalance(r)} /</span>
                              <Input type="number" step="0.5" className="h-7 w-16 text-xs text-center"
                                value={overrides[r.employee_id] ?? r.new_total}
                                onChange={e => setOverrides({ ...overrides, [r.employee_id]: e.target.value })}
                                data-testid={`recalc-total-${r.employee_id}`} />
                            </div>
                          </TableCell>
                          <TableCell className="text-[10px] text-slate-400">
                            {overrides[r.employee_id] !== undefined && overrides[r.employee_id] !== '' && parseFloat(overrides[r.employee_id]) !== r.new_total ? 'manual' : r.source}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500 text-sm">No employees found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleApply} disabled={!preview || applying || applyCount === 0}
              data-testid="recalc-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
              Apply to {applyCount} employee(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
